import { type Color, DestroyAfter, type Scene, Spin, SpriteRenderer, Vector2 } from '@kite/engine';

/** How a burst of sparkles looks. Plain data, so it lives in the game's content file. */
export interface SparkleOptions {
  /** How many fly out. */
  readonly count: number;
  /** How far from the origin they appear, in world units. */
  readonly radius: number;
  /** Edge length of one sparkle, in world units. */
  readonly size: number;
  /** Seconds before a sparkle destroys itself. */
  readonly lifetimeSeconds: number;
  /** Radians per second a sparkle spins at. */
  readonly spinRate: number;
  /** Their color. */
  readonly color: Color;
  /** Draw order they are given. */
  readonly sortOrder: number;
}

/**
 * Throws a ring of short lived sparkles out of a world point.
 *
 * A function rather than a behavior because nothing owns a burst: it belongs to a moment, not to an
 * entity. Each sparkle carries `DestroyAfter`, so the scene cleans itself up with nothing to track and
 * no list to keep.
 */
export function spawnSparkles(scene: Scene, origin: Vector2, options: SparkleOptions): void {
  const { count, radius, size, lifetimeSeconds, spinRate, color, sortOrder } = options;

  for (let index = 0; index < count; index += 1) {
    const angle = (index / count) * Math.PI * 2;
    const sparkle = scene.createEntity(`Sparkle ${String(index + 1)}`);
    sparkle.transform.position.set(origin.x + Math.cos(angle) * radius, origin.y + Math.sin(angle) * radius);
    sparkle.addComponent(new SpriteRenderer({ color: color.clone(), size: new Vector2(size, size), sortOrder }));
    sparkle.addBehavior(new Spin(spinRate));
    sparkle.addBehavior(new DestroyAfter(lifetimeSeconds));
  }
}
